import React, { useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Routes as AppRoutes } from '../../../routes';
import {
  Alert,
  BusinessIcon,
  Button,
  CheckCircleIcon,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  ErrorIcon,
  Spinner,
  Text,
} from '@billda/design-system';
import { getErrorMessage } from '@billda/shared-ui';
import { switchOrganization } from '../../auth/api/authApi';
import { useAuthStore } from '../../auth/stores/authStore';
import { useMultiAccountStore } from '../../auth/stores/multiAccountStore';
import { useQueryClient } from '@tanstack/react-query';
// Import all organization-specific stores that need clearing
import { useSiteImportUIStore } from '../../sites/stores/siteImportUIStore';
import { useUserManagementDataStore } from '../../users/stores/userManagementDataStore';

const OrganizationSwitching: React.FC = () => {
  const { orgId, orgName } = useParams<{ orgId: string; orgName: string }>();
  const navigate = useNavigate();
  const { setTokens } = useAuthStore();
  const queryClient = useQueryClient();

  // Organization-specific stores that need clearing
  const { resetNavState: resetSiteImportNav } = useSiteImportUIStore();
  const userManagementStore = useUserManagementDataStore();

  const [status, setStatus] = useState<
    'confirming' | 'switching' | 'success' | 'error'
  >('confirming');
  const [error, setError] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState(true);
  const hasSwitchedRef = useRef(false);
  const currentOrgIdRef = useRef<string | null>(null);

  // Function to clear all organization-specific state
  const clearAllOrganizationState = () => {
    // Clear React Query cache for organization data (includes sites list)
    // Note: Features query will refetch automatically due to tokenIdentifier-based key
    queryClient.removeQueries();

    // Clear site import nav state
    resetSiteImportNav();

    // Clear user-related state
    userManagementStore.clearError();
  };

  const performSwitch = async () => {
    if (!orgId) {
      setStatus('error');
      setError('Missing organization ID');
      return;
    }

    // Mark as switching to prevent duplicate calls
    hasSwitchedRef.current = true;
    currentOrgIdRef.current = orgId;

    try {
      setStatus('switching');
      setError(null);

      const { getCurrentAccount } = useMultiAccountStore.getState();
      const currentAccount = getCurrentAccount();
      const currentRefreshToken = currentAccount?.refreshToken;

      if (!currentRefreshToken) {
        throw new globalThis.Error('Missing refresh token');
      }

      clearAllOrganizationState();

      // Call the switch organization API
      const response = await switchOrganization({
        organizationId: orgId,
        refreshToken: currentRefreshToken,
      });

      // Update the tokens in the auth store
      setTokens({
        token: response.identityToken,
        refreshToken: response.refreshToken,
      });

      // Update the stored account with the new organization name
      const updatedAccount = useMultiAccountStore
        .getState()
        .getCurrentAccount();
      if (updatedAccount && orgName) {
        useMultiAccountStore.getState().updateAccount(updatedAccount.email, {
          currentOrganisationName: decodeURIComponent(orgName),
        });
      }

      setStatus('success');

      // Redirect to home after a short delay
      setTimeout(() => {
        setIsOpen(false);
        navigate(AppRoutes.root);
      }, 1500);
    } catch (err) {
      console.error('Organization switch error:', err);
      setStatus('error');
      setError(getErrorMessage(err));
      // Reset the flag on error so retry can work
      hasSwitchedRef.current = false;
    }
  };

  const handleConfirmSwitch = () => {
    performSwitch();
  };

  const handleClose = () => {
    setIsOpen(false);
    navigate(-1);
  };

  const handleRetry = () => {
    hasSwitchedRef.current = false;
    currentOrgIdRef.current = null;
    setStatus('confirming');
    setError(null);
  };

  const renderContent = () => {
    switch (status) {
      case 'confirming':
        return (
          <>
            <DialogHeader className="text-center">
              <Text as="span" variant="h5" className="text-content-accent">
                Switch Organization
              </Text>
            </DialogHeader>
            <div className="flex flex-col items-center gap-4 pt-4">
              <Text as="div" variant="h6" className="text-center">
                Are you sure you want to switch to:
              </Text>
              <Text
                as="div"
                variant="h5"
                className="text-center font-bold text-content-secondary"
              >
                {orgName}
              </Text>
              <Text
                as="div"
                variant="body1"
                className="text-center text-content-secondary"
              >
                This will change your current organization context and may
                affect your access to certain features.
              </Text>
            </div>
            <DialogFooter className="justify-center gap-4 pb-6">
              <Button variant="outline" onClick={handleClose} size="lg">
                Cancel
              </Button>
              <Button variant="neutral" onClick={handleConfirmSwitch} size="lg">
                Yes, Switch Organization
              </Button>
            </DialogFooter>
          </>
        );

      case 'switching':
        return (
          <>
            <DialogHeader className="text-center">
              <Text as="span" variant="h5" className="text-content-accent">
                Switching Organization
              </Text>
            </DialogHeader>
            <div className="flex flex-col items-center gap-4 px-6 py-8">
              <Spinner size={60} />
              <Text as="div" variant="h6" className="text-center">
                Switching to {orgName}...
              </Text>
              <Text
                as="div"
                variant="body1"
                className="text-center text-content-secondary"
              >
                Please wait while we switch your organization.
              </Text>
            </div>
          </>
        );

      case 'success':
        return (
          <>
            <DialogHeader className="text-center">
              <Text as="span" variant="h5" className="text-content-success">
                Organization Switched Successfully
              </Text>
            </DialogHeader>
            <div className="flex flex-col items-center gap-4 px-6 py-8">
              <CheckCircleIcon
                aria-hidden="true"
                className="size-[60px] text-content-success"
              />
              <div className="flex items-center justify-center gap-2 rounded-lg border border-border-default bg-surface-raised p-4">
                <BusinessIcon
                  aria-hidden="true"
                  className="size-6 text-content-success"
                />
                <Text
                  as="span"
                  variant="h6"
                  className="font-medium text-content-success"
                >
                  {orgName
                    ? decodeURIComponent(orgName)
                    : 'Unknown Organization'}
                </Text>
              </div>
              <Text as="div" variant="body2" color="secondary">
                Redirecting to home...
              </Text>
            </div>
          </>
        );

      case 'error':
        return (
          <>
            <DialogHeader className="text-center">
              <Text as="span" variant="h5" className="text-content-error">
                Failed to Switch Organization
              </Text>
            </DialogHeader>
            <div className="flex flex-col items-center gap-4 px-6 pt-4">
              <ErrorIcon
                aria-hidden="true"
                className="size-[60px] text-content-error"
              />
              {error && (
                <Alert severity="error" className="w-full text-left">
                  {error}
                </Alert>
              )}
            </div>
            <DialogFooter className="justify-center gap-4 pb-6">
              <Button variant="outline" onClick={handleClose}>
                Close
              </Button>
              <Button variant="neutral" onClick={handleRetry}>
                Try Again
              </Button>
            </DialogFooter>
          </>
        );

      default:
        return null;
    }
  };

  const closable = status === 'switching' || status === 'success';

  return (
    <Dialog
      open={isOpen}
      onOpenChange={
        closable
          ? undefined
          : nextOpen => {
              if (!nextOpen) {
                handleClose();
              }
            }
      }
    >
      <DialogContent className="max-w-lg">{renderContent()}</DialogContent>
    </Dialog>
  );
};

export default OrganizationSwitching;
